import type { FC, ReactNode } from 'react'

import { Fragment, useRef } from 'react'
import Image from 'next/image'

import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

interface PageHeaderProps {
    eyebrow: string
    lines: string[]
    lead?: string
    image: string
    imageAlt: string
    meta?: { label: string; value: string }[]
}

/**
 * Shared opening for the inner pages. Mirrors the home hero's sequencing at a
 * smaller scale, so moving between pages feels like the same site rather than
 * four separate entrances.
 */
export const PageHeader: FC<PageHeaderProps> = ({
    eyebrow,
    lines,
    lead,
    image,
    imageAlt,
    meta
}): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            const frame = element.querySelector<HTMLElement>('[data-header-frame]')
            const picture = element.querySelector<HTMLElement>('[data-header-picture]')
            const words = element.querySelectorAll<HTMLElement>('[data-header-word]')
            const extras = element.querySelectorAll<HTMLElement>('[data-header-extra]')

            gsap.set(element.querySelectorAll('[data-reveal]'), { visibility: 'visible' })

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            gsap.timeline({ delay: 0.1 })
                .fromTo(
                    frame,
                    { clipPath: 'inset(0% 0% 100% 0%)' },
                    { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.5, ease: 'expo.out' },
                    0
                )
                .fromTo(
                    picture,
                    { scale: 1.35 },
                    { scale: 1, duration: 1.9, ease: 'expo.out' },
                    0
                )
                .fromTo(
                    words,
                    { yPercent: 118, rotate: 2 },
                    {
                        yPercent: 0,
                        rotate: 0,
                        duration: 1.2,
                        ease: 'expo.out',
                        stagger: 0.06
                    },
                    0.32
                )
                .fromTo(
                    extras,
                    { y: 24, opacity: 0 },
                    { y: 0, opacity: 1, duration: 0.9, ease: 'power3.out', stagger: 0.07 },
                    0.7
                )

            gsap.to(picture, {
                yPercent: 14,
                ease: 'none',
                scrollTrigger: {
                    trigger: element,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 1
                }
            })
        }, element)

        return () => context.revert()
    }, [])

    return (
        <section ref={root} className='relative isolate overflow-hidden grain bg-[color:var(--ink)]'>
            <div className='absolute inset-0 -z-10'>
                <div data-header-frame className='h-full w-full overflow-hidden'>
                    <div data-header-picture className='relative h-full w-full will-change-transform'>
                        <Image
                            src={image}
                            alt={imageAlt}
                            fill
                            priority
                            sizes='100vw'
                            className='object-cover'
                        />
                    </div>
                </div>

                <div className='absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/70' />
            </div>

            <div className='edge flex min-h-[72svh] flex-col justify-end pb-14 pt-36 md:min-h-[80svh] md:pb-20'>
                <p data-header-extra data-reveal className='label mb-8 text-white/70'>
                    {eyebrow}
                </p>

                <h1 data-reveal className='display-xl max-w-5xl text-white'>
                    {lines.map((line, lineIndex) => (
                        <span key={lineIndex} className='line-mask'>
                            {line.split(' ').map((word, wordIndex) => (
                                <Fragment key={`${lineIndex}-${wordIndex}`}>
                                    {/* Real text node between the words. A space
                                        left inside the inline-block is trailing
                                        whitespace and gets dropped. */}
                                    {wordIndex > 0 ? ' ' : null}

                                    <span
                                        data-header-word
                                        className='inline-block will-change-transform'
                                    >
                                        {word}
                                    </span>
                                </Fragment>
                            ))}
                        </span>
                    ))}
                </h1>

                <div className={cn('mt-12 flex flex-col gap-10 md:flex-row md:items-end md:justify-between')}>
                    {lead && (
                        <p
                            data-header-extra
                            data-reveal
                            className='max-w-xl text-[0.9375rem] leading-relaxed text-white/75'
                        >
                            {lead}
                        </p>
                    )}

                    {meta && (
                        <dl className='flex flex-wrap gap-x-10 gap-y-4'>
                            {meta.map((item) => (
                                <div key={item.label} data-header-extra data-reveal>
                                    <dt className='label text-white/50'>{item.label}</dt>
                                    <dd className='mt-1 text-sm text-white/85'>{item.value}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </div>
            </div>
        </section>
    )
}
